import React, { useState, useContext, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, 
  FolderOpen, 
  Briefcase, 
  Search, 
  MessageSquare, 
  Calendar,
  FileText,
  CreditCard,
  User,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  LifeBuoy,
  Star,
  Handshake,
  BookOpen,
  BarChart3,
  Lock,
  UserCircle,
  Video,
  Shield
} from 'lucide-react';
import { AuthContext } from '../App';
import NotificationBell from './NotificationBell';
import PushNotificationPrompt from './PushNotificationPrompt';
import FreelanceTrialBadge from './FreelanceTrialBadge';
import { useAutoSave } from '../contexts/AutoSaveContext';
import AutoSaveIndicator from './AutoSaveIndicator';
import { useFreelanceRestrictions } from '../hooks/useFreelanceStatus';
import { isRMRole, isOperatingPartnerRole, getRoleDisplayName } from '../utils/roles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RMPMLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [logoTimestamp, setLogoTimestamp] = useState(Date.now()); // For cache busting
  const [onlineCount, setOnlineCount] = useState(0);
  const [profileCompleteness, setProfileCompleteness] = useState(100); // Profile completion percentage
  const [unreadMessageCount, setUnreadMessageCount] = useState(0); // Unread messages count
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const { autoSaving, lastSaved } = useAutoSave();
  
  // Use role utility functions - operating_partner is treated as an RM variant
  const isRM = isRMRole(user);
  const isOperatingPartner = isOperatingPartnerRole(user);
  const isFreelance = user?.is_freelance === true;
  
  // Get freelance restrictions for represented users
  const { 
    isRepresented, 
    canSendMessages, 
    canCreateProposals 
  } = useFreelanceRestrictions();

  useEffect(() => {
    fetchCompanyInfo();
    fetchOnlineCount();
    fetchProfileCompleteness();
    fetchUnreadMessageCount();
    
    // Listen for company info updates
    const handleCompanyUpdate = (event) => {
      // Update timestamp to force logo refresh
      setLogoTimestamp(Date.now());
      fetchCompanyInfo();
    };
    
    // Listen for profile updates
    const handleProfileUpdate = () => {
      fetchProfileCompleteness();
    };
    
    // Listen for messages being read
    const handleMessagesRead = () => {
      fetchUnreadMessageCount();
    };
    
    window.addEventListener('companyInfoUpdated', handleCompanyUpdate);
    window.addEventListener('profileUpdated', handleProfileUpdate);
    window.addEventListener('messagesRead', handleMessagesRead);
    
    // Setup heartbeat for presence tracking (every 2 minutes)
    const heartbeatInterval = setInterval(sendHeartbeat, 120000);
    sendHeartbeat(); // Send immediately on mount
    
    // Fetch online count every minute
    const onlineInterval = setInterval(fetchOnlineCount, 60000);
    
    // Fetch unread message count every 30 seconds
    const messageInterval = setInterval(fetchUnreadMessageCount, 30000);
    
    return () => {
      window.removeEventListener('companyInfoUpdated', handleCompanyUpdate);
      window.removeEventListener('profileUpdated', handleProfileUpdate);
      window.removeEventListener('messagesRead', handleMessagesRead);
      clearInterval(heartbeatInterval);
      clearInterval(onlineInterval);
      clearInterval(messageInterval);
    };
  }, []);

  const sendHeartbeat = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      await axios.post(`${API}/team/heartbeat`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchOnlineCount(); // Update count after heartbeat
    } catch (error) {
      // Silently fail - not critical
    }
  };

  const fetchOnlineCount = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/team/online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineCount(response.data.count || 0);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchProfileCompleteness = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/users/profile-completeness`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileCompleteness(response.data.completeness || 0);
    } catch (error) {
      // Default to 100 if endpoint doesn't exist yet
      setProfileCompleteness(100);
    }
  };
  
  const fetchUnreadMessageCount = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sum up all unread counts from conversations
      const totalUnread = response.data.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
      setUnreadMessageCount(totalUnread);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchCompanyInfo = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/companies/my_profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.logo_url) {
        setCompanyLogo(`${BACKEND_URL}${response.data.logo_url}`);
      }
      if (response.data.name) {
        setCompanyName(response.data.name);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  // Main features based on role - Freelancers have adapted labels and restrictions
  const mainFeatures = isRM ? [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { 
      path: '/resources', 
      icon: isFreelance ? UserCircle : FolderOpen, 
      label: isFreelance ? 'My Consultant Profile' : 'Resources' 
    },
    { 
      path: '/matching', 
      icon: Search, 
      label: 'Matching',
      hidden: isRepresented  // Hide completely for represented freelancers
    },
    { 
      path: '/favorites', 
      icon: Star, 
      label: 'My Favorites',
      hidden: isRepresented  // Hide completely for represented freelancers
    },
    { path: '/reservations', icon: Calendar, label: 'Reservations' },
    { path: '/interviews', icon: Video, label: 'Interviews' },
    { path: '/collaborations', icon: Handshake, label: 'Collaborations' },
    { 
      path: '/messages', 
      icon: MessageSquare, 
      label: 'Messages',
      badge: unreadMessageCount  // Show unread message count
      // Messages is now always accessible for represented freelancers
      // Restrictions are handled in the backend and Messages page
    }
  ] : [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/projects', icon: Briefcase, label: 'Projects' },
    { path: '/matching', icon: Search, label: 'Matching' },
    { path: '/favorites', icon: Star, label: 'My Favorites' },
    { path: '/reservations', icon: Calendar, label: 'Reservations' },
    { path: '/interviews', icon: Video, label: 'Interviews' },
    { path: '/collaborations', icon: Handshake, label: 'Collaborations' },
    { 
      path: '/messages', 
      icon: MessageSquare, 
      label: 'Messages',
      badge: unreadMessageCount  // Show unread message count
    }
  ];

  // Profile & settings - Freelancers get different pricing page
  // Hide Analytics for represented freelancers (limited useful data)
  const profileItems = [
    { path: '/getting-started', icon: BookOpen, label: 'Getting Started', showIfNew: true },
    { 
      path: '/analytics', 
      icon: BarChart3, 
      label: 'Analytics',
      hidden: isRepresented  // Hide for represented freelancers
    },
    { path: isFreelance ? '/freelance/choose-model' : '/pricing', icon: CreditCard, label: 'Pricing & Plans' },
    { path: '/profile', icon: User, label: 'Profile', showIncompleteIndicator: true },
    { path: '/contract-templates', icon: FileText, label: 'Contract Templates' },
    { path: '/confidentiality', icon: Shield, label: 'Confidentiality' },
    { path: '/support', icon: LifeBuoy, label: 'Support' }
  ];
  
  // Check if profile is incomplete (less than 80% complete)
  const isProfileIncomplete = profileCompleteness < 80;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <div 
        className={`${collapsed ? 'w-20' : 'w-64'} bg-white shadow-lg flex flex-col transition-all duration-300 border-r-2 border-emerald-500`}
      >
        {/* Logo/Header */}
        <div className="p-6 flex items-center justify-between" style={{ backgroundColor: '#1B3240' }}>
          {!collapsed && (
            <div className="flex items-center gap-3">
              {companyLogo ? (
                <img 
                  src={`${companyLogo}?t=${logoTimestamp}`} 
                  alt="Company Logo" 
                  className="w-10 h-10 object-contain rounded"
                  key={logoTimestamp}
                />
              ) : (
                <div className="w-10 h-10 rounded flex items-center justify-center bg-gradient-to-r from-emerald-500 to-teal-500">
                  <span className="text-white font-bold text-lg">SE</span>
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-white">
                  {companyName || (isRM ? 'RM Portal' : 'PM Portal')}
                </h1>
                <p className="text-xs text-gray-300 mt-1">Snow Exchange</p>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="w-full flex justify-center">
              {companyLogo ? (
                <img 
                  src={`${companyLogo}?t=${logoTimestamp}`} 
                  alt="Logo" 
                  className="w-8 h-8 object-contain rounded"
                  key={logoTimestamp}
                />
              ) : (
                <div className="w-8 h-8 rounded flex items-center justify-center bg-gradient-to-r from-emerald-500 to-teal-500">
                  <span className="text-white font-bold text-xs">SE</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Toggle Button with User Type */}
        <div className="px-4 py-3 border-b flex items-center justify-between">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">
                {isRM ? 'Resource Manager' : 'Project Manager'}
              </span>
            </div>
          )}
          {collapsed && (
            <div className="w-full flex justify-center">
              <span className="text-sm font-bold text-gray-700">
                {isRM ? 'RM' : 'PM'}
              </span>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Collapse sidebar"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>
        
        {/* Expand button when collapsed */}
        {collapsed && (
          <div className="px-2 py-2 border-b flex justify-center">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Expand sidebar"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        )}

        {/* Navigation - Main Features */}
        <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
          {/* Main Features Section */}
          <div className="space-y-1">
            {!collapsed && (
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Main Features
              </p>
            )}
            {mainFeatures.map((item) => {
              const Icon = item.icon;
              const active = item.exact 
                ? location.pathname === item.path 
                : isActive(item.path);
              const isDisabled = item.disabled === true;
              const isHidden = item.hidden === true;
              
              // Hidden items are not rendered at all
              if (isHidden) {
                return null;
              }
              
              // For disabled items, render a div instead of Link
              if (isDisabled) {
                return (
                  <div
                    key={item.path}
                    className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-colors cursor-not-allowed opacity-50`}
                    title={collapsed ? `${item.label} - ${item.disabledReason}` : item.disabledReason}
                  >
                    <div className="relative">
                      <Icon className="w-5 h-5 flex-shrink-0 text-gray-400" />
                      <Lock className="w-3 h-3 absolute -bottom-1 -right-1 text-gray-500" />
                    </div>
                    {!collapsed && (
                      <span className="text-gray-400">{item.label}</span>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-colors relative ${
                    active
                      ? 'text-white font-medium bg-gradient-to-r from-emerald-500 to-teal-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title={collapsed ? item.label : ''}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {/* Badge for unread messages when collapsed */}
                    {item.badge > 0 && collapsed && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                  {!collapsed && (
                    <span className="flex-1">{item.label}</span>
                  )}
                  {/* Badge for unread messages when expanded */}
                  {!collapsed && item.badge > 0 && (
                    <span className="px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Profile & Settings Section */}
          <div className="space-y-1">
            {!collapsed && (
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Account
              </p>
            )}
            {profileItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const showRedDot = item.showIncompleteIndicator && isProfileIncomplete;
              const isHidden = item.hidden === true;
              
              // Hidden items are not rendered at all
              if (isHidden) {
                return null;
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-colors relative ${
                    active
                      ? 'text-white font-medium bg-gradient-to-r from-emerald-500 to-teal-500'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title={collapsed ? item.label : ''}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {/* Red dot indicator for incomplete profile */}
                    {showRedDot && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse" />
                    )}
                  </div>
                  {!collapsed && (
                    <span className="flex-1">{item.label}</span>
                  )}
                  {/* Additional badge for incomplete profile when not collapsed */}
                  {!collapsed && showRedDot && (
                    <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">
                      {profileCompleteness}%
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t">
          <button
            onClick={handleLogout}
            className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 w-full transition-colors`}
            title={collapsed ? 'Logout' : ''}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto flex flex-col">
        {/* Top Header with Auto-save, Online Status & Bell */}
        <div className="bg-white border-b px-6 py-3 flex items-center justify-end shadow-sm">
          <div className="flex items-center gap-4">
            <AutoSaveIndicator autoSaving={autoSaving} lastSaved={lastSaved} />
            {/* Online Indicator */}
            {onlineCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="relative">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                </div>
                <span className="text-xs font-medium text-green-700">
                  {onlineCount} {onlineCount === 1 ? 'member' : 'members'} online
                </span>
              </div>
            )}
            
            {/* Freelance Trial Badge */}
            {isFreelance && <FreelanceTrialBadge />}
            
            <NotificationBell />
          </div>
        </div>
        
        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
      
      {/* Push Notification Prompt */}
      <PushNotificationPrompt />
    </div>
  );
};

export default RMPMLayout;
